import queryString from "query-string";
import {
	__,
	gt,
	gte,
	has,
	isEmpty,
	map,
	pathOr,
	pickBy,
	pipe,
	where,
} from "ramda";

import {
	DEFAULT_NUMBER_OF_COMPANIES_TO_RENDER,
	MINIMUM_QUERY_STRING_COUNT_VALUE_FOR_PAGINATION,
	SITE_INFORMATION,
} from "../../config/constants";

export const returnPaginationInformation = (
	location,
	vertical,
	isTargetedPath,
) => {
	if (!location) {
		throw new Error(
			"location parameter not passed to helper function parseQueryStringForPagination",
		);
	}

	if (!vertical) {
		throw new Error(
			"vertical parameter not passed to helper function parseQueryStringForPagination",
		);
	}

	const numberOfCompaniesToRenderKey = isTargetedPath
		? "targeted"
		: "nonTargeted";

	const paginationInformation = {
		/* Page prop starts at an index of 1 as opposed to 0 */
		page: 1,
		count: pathOr(
			DEFAULT_NUMBER_OF_COMPANIES_TO_RENDER,
			[vertical, "numberOfCompaniesToRender", numberOfCompaniesToRenderKey],
			SITE_INFORMATION,
		),
		isInvalidQuery: false,
		redirectLocationSearch: "",
		partialQueryObjectForPaginationClicks: {},
	};

	/* Create an object from location.search using query-string package */
	const partiallyParsedQueryObject = queryString.parse(location.search);

	/* Used for retaining query parameters after pagination navigation*/
	const queryObjectForPaginationNavigationLinks = {
		...partiallyParsedQueryObject,
	};
	/* We remove page and count properties as they are added via handlePageClick() in <CompanyList/>*/
	delete queryObjectForPaginationNavigationLinks.count;
	delete queryObjectForPaginationNavigationLinks.page;
	paginationInformation.partialQueryObjectForPaginationClicks =
		queryObjectForPaginationNavigationLinks;

	/* Predicate used to return a partial object copy containing only properties where the value is a number or can be converted to a number */
	const getNumericAndConvertibleToNumericProps = pickBy(
		(value) =>
			!Number.isNaN(Number(value)) &&
			typeof value === "string" &&
			value.trim() !== "",
	);
	/* Map over object and convert values to a number*/
	const convertToNumber = map((value) => Number(value));
	/* Left-to-right composition via pipe. Auto-curried so we're not passing any params*/
	const parseNumbers = pipe(
		getNumericAndConvertibleToNumericProps,
		convertToNumber,
	);
	const queryObject = parseNumbers(partiallyParsedQueryObject);

	/* Boolean is true when no query string is part of the url */
	const urlContainsQueryParams = !isEmpty(partiallyParsedQueryObject);

	const queryPredicate = where({
		page: gt(__, 0),
		count: gte(__, MINIMUM_QUERY_STRING_COUNT_VALUE_FOR_PAGINATION),
	});

	/* Boolean indicating if a pagination query params are valid values */
	const validPaginationQueryParams = queryPredicate(queryObject);

	const queryHasPage = has("page");
	const queryHasCount = has("count");
	const queryHasPageAndCount =
		queryHasPage(queryObject) && queryHasCount(queryObject);

	/* If the pagination query params are valid use their values*/
	if (validPaginationQueryParams) {
		paginationInformation.page = queryObject.page;
		paginationInformation.count = queryObject.count;
	}

	/* If the URL contains query params that include 'page' and 'count' but their values are out of range, we redirect to a valid URL containing
	 * pagination query params that meet the predicate */
	if (
		urlContainsQueryParams &&
		queryHasPageAndCount &&
		!validPaginationQueryParams
	) {
		paginationInformation.isInvalidQuery = true;
		/* New object that will be used for invalid query redirects. Copy of the original so we retain query parameters*/
		const redirectQueryObject = { ...partiallyParsedQueryObject };
		/* Redefining page and count properties so they're values are the correct defaults */
		redirectQueryObject["page"] = paginationInformation.page;
		redirectQueryObject["count"] = paginationInformation.count;
		/* convert object to string for use by <Redirect> in <CompanyList>*/
		paginationInformation.redirectLocationSearch =
			queryString.stringify(redirectQueryObject);
	}

	return paginationInformation;
};
